import { formatDateFilterToken } from 'utils/dateUtils';
import {
  CLAIM_TARIFF_STATUS,
  READY_SUBMISSION_FILTER_ID,
  PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID,
} from '@billing/validation/tariff';
import { DateTime } from 'luxon';
import type {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
} from 'mantine-react-table';
import { isMeaningfulFilterValue } from 'utils/filterUtils';
import {
  getLatestNestedPayorDebit,
  isClaimParentRow,
  type ClaimTableRow,
} from './claimTableRows';

export type ClaimTariffFilterMatcher = (
  rowValue: unknown,
  filterValues: unknown[]
) => boolean;

export interface ClaimTariffFilterDefinition {
  scope: 'tariff';
  includeColumnID?: boolean;
  aliases?: string[];
  getValue: (row: ClaimTableRow) => unknown;
  match: ClaimTariffFilterMatcher;
}

export const defineClaimTariffFilter = (
  definition: ClaimTariffFilterDefinition
): ClaimTariffFilterDefinition => definition;

interface ClaimColumnMeta {
  tariffFilter?: ClaimTariffFilterDefinition;
}

export interface CompiledClaimTariffFilter {
  definition: ClaimTariffFilterDefinition;
  values: unknown[];
}

const getComparableValues = (value: unknown): unknown[] =>
  Array.isArray(value) ? value.flatMap(getComparableValues) : [value];

const normalizeText = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

export const matchAllTextFilters: ClaimTariffFilterMatcher = (
  rowValue,
  filterValues
) => {
  const rowText = getComparableValues(rowValue).map(normalizeText);
  return getComparableValues(filterValues)
    .filter(isMeaningfulFilterValue)
    .every((filterValue) =>
      rowText.some((value) => value.includes(normalizeText(filterValue)))
    );
};

export const matchAnyExactFilter: ClaimTariffFilterMatcher = (
  rowValue,
  filterValues
) => {
  const rowValues = getComparableValues(rowValue).map(normalizeText);
  return getComparableValues(filterValues)
    .filter(isMeaningfulFilterValue)
    .some((filterValue) => rowValues.includes(normalizeText(filterValue)));
};

export const matchAllNumericFilters: ClaimTariffFilterMatcher = (
  rowValue,
  filterValues
) => {
  const rowValues = getComparableValues(rowValue)
    .filter(isMeaningfulFilterValue)
    .map(Number)
    .filter((value) => !Number.isNaN(value));

  return getComparableValues(filterValues)
    .filter(isMeaningfulFilterValue)
    .every((filterValue) => {
      const numericFilter = Number(filterValue);
      return !Number.isNaN(numericFilter) && rowValues.includes(numericFilter);
    });
};

export const matchPaidAmountFilters: ClaimTariffFilterMatcher = (
  rowValue,
  filterValues
) => {
  const paidAmounts = getComparableValues(rowValue)
    .filter(isMeaningfulFilterValue)
    .map(Number)
    .filter((value) => !Number.isNaN(value));

  return getComparableValues(filterValues)
    .filter(isMeaningfulFilterValue)
    .every((filterValue) => {
      const numericFilter = Number(filterValue);
      if (Number.isNaN(numericFilter)) return false;
      if (numericFilter === 0) return paidAmounts.includes(0);

      const searchTerm = normalizeText(filterValue);
      return paidAmounts.some((paidAmount) =>
        normalizeText(paidAmount).includes(searchTerm)
      );
    });
};

export const matchLatestEOBCodeFilters: ClaimTariffFilterMatcher = (
  rowValue,
  filterValues
) => {
  const rowCodes = getComparableValues(rowValue).map(normalizeText);

  return filterValues.every((filterValue) => {
    const acceptedCodes = getComparableValues(filterValue)
      .flatMap((value) => String(value).split('|'))
      .map(normalizeText)
      .filter(Boolean);

    return acceptedCodes.some((code) => rowCodes.includes(code));
  });
};

const getDisplayedTariffID = (row: ClaimTableRow): string | undefined =>
  row.rowType === 'tariff' ? row.tariffId : undefined;

const getLatestFilterDebit = (row: ClaimTableRow) => {
  const tariffID = getDisplayedTariffID(row);
  return tariffID ? getLatestNestedPayorDebit(row, tariffID) : null;
};

export const getLatestDebitPaidAmountFilterValue = (
  row: ClaimTableRow
): number | undefined => {
  const latestDebit = getLatestFilterDebit(row);
  if (
    latestDebit?.paidAmount === null ||
    latestDebit?.paidAmount === undefined
  ) {
    return undefined;
  }
  return Number(latestDebit.paidAmount);
};

export const getLatestDebitRequestedAmountFilterValue = (
  row: ClaimTableRow
): number | undefined => {
  const latestDebit = getLatestFilterDebit(row);
  if (
    latestDebit?.requestedAmount === null ||
    latestDebit?.requestedAmount === undefined
  ) {
    return undefined;
  }
  return Number(latestDebit.requestedAmount);
};

export const getLatestDebitDifferenceAmountFilterValue = (
  row: ClaimTableRow
): number | undefined => {
  const latestDebit = getLatestFilterDebit(row);
  if (!latestDebit) return undefined;

  return (
    Number(latestDebit.paidAmount ?? 0) -
    Number(latestDebit.requestedAmount ?? 0)
  );
};

export const getLatestDebitEOBCodeFilterValue = (
  row: ClaimTableRow
): string[] =>
  (getLatestFilterDebit(row)?.eobs ?? []).map((eob) => String(eob.code));

export const getLatestDebitEOBDescriptionFilterValue = (
  row: ClaimTableRow
): string[] =>
  (getLatestFilterDebit(row)?.eobs ?? []).map((eob) =>
    String(eob.description ?? '')
  );

export const getLatestDebitMicrofilmFilterValue = (
  row: ClaimTableRow
): string | undefined => getLatestFilterDebit(row)?.microfilm ?? undefined;

const getColumnFilterDefinition = (
  column: MRT_ColumnDef<ClaimTableRow>
): ClaimTariffFilterDefinition | undefined =>
  (column.meta as ClaimColumnMeta | undefined)?.tariffFilter;

const getColumnFilterIDs = (
  column: MRT_ColumnDef<ClaimTableRow>,
  definition: ClaimTariffFilterDefinition
): string[] => {
  const primaryID = String(column.id ?? column.accessorKey ?? '').trim();
  const filterIDs = definition.includeColumnID === false ? [] : [primaryID];
  return [...filterIDs, ...(definition.aliases ?? [])].filter(Boolean);
};

const flattenColumns = (
  columns: MRT_ColumnDef<ClaimTableRow>[]
): MRT_ColumnDef<ClaimTableRow>[] =>
  columns.flatMap((column) => [
    column,
    ...flattenColumns(column.columns ?? []),
  ]);

const dashboardBilledDateFilter: ClaimTariffFilterDefinition = {
  scope: 'tariff',
  getValue: (row) => {
    const tariff = row.tariffs[0];
    const dates = (): string[] => {
      if (row.payor?.type === 'PUBLIC')
        return (
          tariff.submissionDates ??
          [tariff.lastSubmittedAt].filter((date): date is string =>
            Boolean(date)
          )
        );
      if (row.payor?.type === 'DIRECT')
        return (row.debits ?? [])
          .filter(
            (debit) =>
              debit.tariffID === tariff.id && debit.payorID === row.payorID
          )
          .map((debit) => debit.createdAt);
      return [row.serviceStart];
    };
    return dates().flatMap((value) => {
      const date = DateTime.fromISO(value, { zone: 'utc' })
        .setZone(row.site.timezone)
        .toISODate();
      return date ? [date] : [];
    });
  },
  match: (rowValue, filterValues) =>
    filterValues.every((value) => {
      const range = Array.isArray(value) ? value : String(value).split('~');
      const [start, end] = range.map(formatDateFilterToken);
      return (
        Boolean(start && end) &&
        getComparableValues(rowValue).some(
          (date) => typeof date === 'string' && date >= start && date <= end
        )
      );
    }),
};

const readySubmissionFilter: ClaimTariffFilterDefinition = {
  scope: 'tariff',
  getValue: (row) =>
    row.tariffs[0].status === CLAIM_TARIFF_STATUS.READY
      ? String(Boolean(row.tariffs[0].lastSubmittedAt))
      : null,
  match: (rowValue, values) =>
    rowValue === null || matchAnyExactFilter(rowValue, values),
};

export const compileClaimTariffFilters = (
  columns: MRT_ColumnDef<ClaimTableRow>[],
  columnFilters: MRT_ColumnFiltersState
): CompiledClaimTariffFilter[] => {
  const definitionsByFilterID = new Map<string, ClaimTariffFilterDefinition>([
    [PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID, dashboardBilledDateFilter],
    [READY_SUBMISSION_FILTER_ID, readySubmissionFilter],
  ]);

  flattenColumns(columns).forEach((column) => {
    const definition = getColumnFilterDefinition(column);
    if (!definition || definition.scope !== 'tariff') return;
    getColumnFilterIDs(column, definition).forEach((filterID) => {
      definitionsByFilterID.set(filterID, definition);
    });
  });

  const valuesByDefinition = new Map<ClaimTariffFilterDefinition, unknown[]>();
  columnFilters
    .filter((filter) => isMeaningfulFilterValue(filter.value))
    .forEach((filter) => {
      const definition = definitionsByFilterID.get(filter.id);
      if (!definition) return;
      const values = valuesByDefinition.get(definition) ?? [];
      values.push(filter.value);
      valuesByDefinition.set(definition, values);
    });

  return Array.from(valuesByDefinition, ([definition, values]) => ({
    definition,
    values,
  }));
};

export const filterClaimTariffSubRows = (
  rows: ClaimTableRow[],
  compiledFilters: CompiledClaimTariffFilter[],
  editingClaimID?: string
): ClaimTableRow[] => {
  if (!compiledFilters.length) return rows;

  return rows.reduce<ClaimTableRow[]>((filteredRows, row) => {
    if (!isClaimParentRow(row) || row.id === editingClaimID) {
      filteredRows.push(row);
      return filteredRows;
    }
    if (!row.subRows.length) return filteredRows;

    const subRows = row.subRows.filter((subRow) =>
      compiledFilters.every(({ definition, values }) =>
        definition.match(definition.getValue(subRow), values)
      )
    );

    if (!subRows.length) return filteredRows;
    filteredRows.push(
      subRows.length === row.subRows.length ? row : { ...row, subRows }
    );
    return filteredRows;
  }, []);
};

export const applyClaimTariffRowFilters = (
  rows: ClaimTableRow[],
  columns: MRT_ColumnDef<ClaimTableRow>[],
  columnFilters: MRT_ColumnFiltersState,
  enabled: boolean,
  editingClaimID?: string
): ClaimTableRow[] => {
  if (!enabled) return rows;
  return filterClaimTariffSubRows(
    rows,
    compileClaimTariffFilters(columns, columnFilters),
    editingClaimID
  );
};
